import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { companyInfo, services } from '../data/mock';
import { toast } from 'sonner';
import { trackUserActivity } from '../hooks/useMonitoring';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prefillKey, setPrefillKey] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const requested = location.state?.service;
    if (!requested) return;
    const quoteLines = location.state?.quoteLines;
    const match = services.find((s) => requested.startsWith(s.title));
    const defaultMessage = quoteLines?.length
      ? `I'd like a quote for the following bundle:\n- ${quoteLines.join('\n- ')}\n\nPlease confirm pricing and next steps.`
      : `I'm interested in the ${requested} package. Please send me a quote.`;
    const customer = location.state?.customer || {};
    setFormData(prev => ({
      ...prev,
      name: prev.name || customer.name || '',
      email: prev.email || customer.email || '',
      phone: prev.phone || customer.phone || '',
      company: prev.company || customer.company || '',
      service: match ? match.title : prev.service,
      message: prev.message || defaultMessage
    }));
    setPrefillKey((k) => k + 1);
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleBudgetChange = (value) => {
    setFormData(prev => ({ ...prev, budget: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track form submission attempt
    trackUserActivity('contact_form_submit_started', {
      service: formData.service,
      budget: formData.budget
    });

    try {
      // Submit to backend API
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        service: formData.service || null,
        budget: formData.budget || null,
        message: formData.message
      });

      if (response.data.success) {
        // Track successful submission
        trackUserActivity('contact_form_submit_success', {
          service: formData.service,
          budget: formData.budget,
          submissionId: response.data.id
        });

        toast.success('Thank you for your inquiry!', {
          description: response.data.message,
          icon: <CheckCircle2 className="w-5 h-5" />
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          budget: '',
          message: ''
        });
      }
    } catch (error) {
      // Log only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Contact form submission error:', error);
      }
      
      // Track failed submission
      trackUserActivity('contact_form_submit_failed', {
        error: error.message
      });

      const errorMessage = error.response?.data?.detail || 
                          'Failed to send your inquiry. Please try again or email us directly at chidi@iwurks360.com';

      toast.error('Submission Failed', {
        description: errorMessage,
        icon: <AlertCircle className="w-5 h-5" />
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            Let's Start Your Transformation
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to grow your business? Get in touch with us today for a free consultation
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-gray-200 bg-white">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Contact Information
                </CardTitle>
                <CardDescription>
                  Reach out to us through any of these channels
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div style={{ backgroundColor: '#D97904' }} className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-gray-900 transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div style={{ backgroundColor: '#D97904' }} className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-gray-900 transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div style={{ backgroundColor: '#D97904' }} className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                    <p className="text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 text-white" style={{ background: 'linear-gradient(to bottom right, #D97904, #B86304)' }}>
              <CardContent className="p-6">
                <h4 className="font-bold text-xl mb-2">Quick Response Guarantee</h4>
                <p className="text-orange-50 text-sm">
                  We respond to all inquiries within 24 hours. For urgent matters, call us directly.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-gray-200 bg-white shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Request a Free Consultation
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you shortly
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="border-gray-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        required
                        className="border-gray-300"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 123-4567"
                        className="border-gray-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Inc."
                        className="border-gray-300"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="service">Service Interested In</Label>
                      <Select key={prefillKey} value={formData.service} onValueChange={handleServiceChange}>
                        <SelectTrigger className="border-gray-300" data-testid="contact-service-select">
                          <SelectValue placeholder="Select a service">{formData.service || undefined}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.title}>
                              {service.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="budget">Estimated Budget</Label>
                      <Select value={formData.budget} onValueChange={handleBudgetChange}>
                        <SelectTrigger className="border-gray-300">
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-5k">Under $5,000</SelectItem>
                          <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                          <SelectItem value="10k-25k">$10,000 - $25,000</SelectItem>
                          <SelectItem value="25k-50k">$25,000 - $50,000</SelectItem>
                          <SelectItem value="50k-plus">$50,000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Tell Us About Your Project *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Describe your business goals, challenges, and what you're looking to achieve..."
                      rows={5}
                      required
                      className="border-gray-300 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    style={{ backgroundColor: '#D97904' }}
                    className="w-full text-white hover:opacity-90 transition-all duration-200 py-6 text-lg group"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                    <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
